'use client';

/**
 * Toast Component
 *
 * Simple toast notifications using Radix UI
 */

import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { COLORS } from '@/lib/constants/castorama';

// ============================================================================
// TYPES
// ============================================================================

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
}

interface ToastContextValue {
  toast: (type: ToastType, title: string, description?: string) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// ============================================================================
// PROVIDER
// ============================================================================

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);

  const addToast = React.useCallback((type: ToastType, title: string, description?: string) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, type, title, description }]);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = React.useMemo<ToastContextValue>(() => ({
    toast: addToast,
    success: (title, description) => addToast('success', title, description),
    error: (title, description) => addToast('error', title, description),
    info: (title, description) => addToast('info', title, description),
    warning: (title, description) => addToast('warning', title, description),
  }), [addToast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <ToastPrimitive.Root
            key={toast.id}
            className={`toast-root toast-${toast.type}`}
            onOpenChange={(open) => {
              if (!open) removeToast(toast.id);
            }}
            duration={5000}
          >
            <div className="toast-content">
              <div className="toast-icon">{getIcon(toast.type)}</div>
              <div className="toast-text">
                <ToastPrimitive.Title className="toast-title">
                  {toast.title}
                </ToastPrimitive.Title>
                {toast.description && (
                  <ToastPrimitive.Description className="toast-description">
                    {toast.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              <ToastPrimitive.Close className="toast-close">
                ×
              </ToastPrimitive.Close>
            </div>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="toast-viewport" />
      </ToastPrimitive.Provider>

      <style jsx global>{`
        .toast-viewport {
          position: fixed;
          bottom: 24px;
          right: 24px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: 380px;
          max-width: 100vw;
          z-index: 9999;
          outline: none;
        }

        .toast-root {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 14px 16px;
          border-left: 4px solid;
          animation: slideIn 0.2s ease-out;
        }

        .toast-root[data-state='closed'] {
          animation: slideOut 0.2s ease-in;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .toast-success {
          border-color: ${COLORS.success};
        }

        .toast-error {
          border-color: ${COLORS.danger};
        }

        .toast-info {
          border-color: ${COLORS.primary};
        }

        .toast-warning {
          border-color: ${COLORS.warning};
        }

        .toast-content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .toast-icon {
          font-size: 18px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .toast-text {
          flex: 1;
          min-width: 0;
        }

        .toast-title {
          font-size: 14px;
          font-weight: 600;
          color: ${COLORS.textPrimary};
          margin: 0;
        }

        .toast-description {
          font-size: 13px;
          color: ${COLORS.textSecondary};
          margin: 4px 0 0 0;
          line-height: 1.4;
        }

        .toast-close {
          background: none;
          border: none;
          font-size: 20px;
          color: ${COLORS.textMuted};
          cursor: pointer;
          padding: 0;
          line-height: 1;
          flex-shrink: 0;
        }

        .toast-close:hover {
          color: ${COLORS.textPrimary};
        }
      `}</style>
    </ToastContext.Provider>
  );
}

function getIcon(type: ToastType): string {
  switch (type) {
    case 'success':
      return '✓';
    case 'error':
      return '✕';
    case 'info':
      return 'ℹ';
    case 'warning':
      return '⚠';
    default:
      return '';
  }
}
